import ScreenLayout from '@/components/layout/ScreenLayout'
import Skeleton from '@/components/ui/Skeleton'
import AccountPageHeader from '@/screens/account/components/AccountPageHeader'
import SubscriptionSteps from '@/screens/account/components/SubscriptionSteps'

/**
 * A plan card's tagline and three ticks while its flags load — each bar
 * centred in the loaded line's own box (`h-lh` of `text-body-s`, the
 * Checklist's `sm` size), so the card does not grow when they land.
 */
export function PlanFeaturesSkeleton() {
  return (
    <div aria-hidden="true">
      <div className="mt-2 flex h-lh items-center text-body-xs">
        <Skeleton className="h-3 w-3/4" />
      </div>
      <div className="mt-4 space-y-2 text-body-m">
        {[0, 1, 2].map((line) => (
          <div key={line} className="flex h-lh items-center gap-2">
            <Skeleton className="size-4" />
            <Skeleton className="h-3 w-2/3" />
          </div>
        ))}
      </div>
    </div>
  )
}

function PlanCardSkeleton() {
  return (
    <div className="flex h-full min-h-108 flex-col bg-card p-5 @max-sm:p-4">
      <div className="flex h-30 items-start justify-center pt-3">
        <Skeleton className="size-24 rounded-full" />
      </div>
      <div className="mt-4 flex h-6 items-center">
        <Skeleton className="h-4 w-20" />
      </div>
      <div className="mt-2 flex h-lh items-center text-h-xl">
        <Skeleton className="h-6 w-32" />
      </div>
      <PlanFeaturesSkeleton />
      <div className="min-h-4 flex-1" />
      <Skeleton className="h-12 w-36" />
    </div>
  )
}

/**
 * `/subscription` before the catalogue and the member's plan have loaded: the
 * loaded page's blocks in its order, with its own box classes — the steps
 * (static, so the real ones), the banner at a Free member's one line, the
 * period switch's 40px track, and three cards. The title row is the real one.
 *
 * Each card is the typical paid one, measured against the loaded page at 1440
 * (427px): the price with its run wrapped under it ("every 3 months · Rs 450
 * a month" does not fit beside the price in a third of the column), a
 * tagline and three ticks. A card whose plan names no features (dev's
 * Sadasya) loads shorter; that is the data, not the skeleton.
 */
export default function PlansPending() {
  return (
    <ScreenLayout header={<AccountPageHeader title="Subscription" />} fullWidth pending>
      <SubscriptionSteps current="pick" />
      <div className="flex min-h-17 items-center gap-4 bg-card px-5 py-3">
        <Skeleton className="size-10 rounded-full" />
        <div className="flex h-lh items-center text-h-s">
          <Skeleton className="h-3 w-32" />
        </div>
      </div>
      <div className="flex flex-col items-center gap-2">
        <Skeleton className="h-10 w-60" />
        <div className="h-4" />
      </div>
      <div className="grid gap-6 pt-2 @3xl:grid-cols-3">
        <PlanCardSkeleton />
        <PlanCardSkeleton />
        <PlanCardSkeleton />
      </div>
    </ScreenLayout>
  )
}
